import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import CartSummary from "./CartSummary";
import { useNavigate } from "react-router-dom";
import btnArrow from "../assets/img/section-btn-arrow.webp";
import pinIcon from "../assets/img/pin.webp";
import matchIcon from "../assets/img/match.webp";
import measureIcon from "../assets/img/measure.webp";
import useCartStore from "../api/cart";
import { fitMethodsFor, serviceForFit } from "../utils/fitMethods.js";

/** The icon for each method; the list itself lives in utils/fitMethods.js
 *  so the rule about which methods a service offers can be tested. */
const ICONS = {
  Measure: measureIcon,
  "Pin Items": pinIcon,
  "Match Items": matchIcon,
};

export const NOTES_PLACEHOLDER = {
  Measure: 'e.g. Take 2" off the hem. Inseam 27".',
  "Pin Items": 'e.g. Pinned at both side seams, about 1" each side.',
  "Match Items": "e.g. Matching to the customer's black high-waisted jeans.",
};

/**
 * Fit details for an alteration, taken by staff at the counter.
 *
 * The customer-facing version of this screen carried tutorial videos, "book a
 * virtual consultation" links and "I confirm I pinned it" checkboxes; staff
 * don't need any of that. What remains is the part the fixers rely on: how
 * the fit was captured (Measure / Pin Items / Match Items).
 *
 * No notes box here since 2026-09-30: the next screen's "Notes to our
 * tailors" is the one notes box for every service, and the only one the
 * sticker prints. Two boxes asking for the same thing ("Take 2 inches off
 * the hem") split the notes between them. The method's example text is
 * handed on to that box instead (`fitMethod`).
 *
 * The saved shape is unchanged — `service.alterationDetails = { type,
 * measurement }` — because the backend stores the service as-is and both
 * admin panels read those two fields (superadmin-v2 `alterationSummary`, and
 * its edit form only accepts these three type names).
 *
 * A service with no capture methods never reaches this screen: it is sent
 * straight on to the notes screen, because asking "how was the fit
 * captured?" with no buttons under it is a dead end (31 Oct 2026). Which
 * methods a service offers is utils/fitMethods.js, where it is tested.
 */
const PerfectFit = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [isOpen1, setIsOpen1] = useState(true);

  const toggleBox = () => {
    setIsOpen(!isOpen);
    setIsOpen1(!isOpen1);
  };
  const [selectedOptionPrice] = useState(
    () => sessionStorage.getItem("optionprice") || "",
  );

  const { cartItems, updateCartItem } = useCartStore();
  const location = useLocation();
  const navigate = useNavigate();

  // null until the service is found, then the methods it offers.
  const [infoModes, setInfoModes] = useState(null);
  const [method, setMethod] = useState("Measure");

  // null until the lookup has run, so the buttons are not drawn empty for
  // a frame; an empty list means we are on our way to the notes screen.
  const availableMethods = infoModes || [];

  useEffect(() => {
    const service = serviceForFit(
      cartItems[0]?.services,
      location?.state?.selectedOption,
      sessionStorage.getItem("option"),
    );
    if (!service) return;
    const methods = fitMethodsFor(service);
    setInfoModes(methods);
    // Nothing to ask: a custom write-in carries no info_mode and some menu
    // services carry an empty one. Skip straight to the notes screen rather
    // than show "How was the fit captured?" with no way to answer.
    if (methods.length === 0) {
      navigate("/know-more", {
        replace: true,
        state: {
          selectedOption:
            location.state?.selectedOption || sessionStorage.getItem("option"),
          selectedTask:
            location.state?.selectedTask || sessionStorage.getItem("task"),
          data: location.state?.data,
        },
      });
      return;
    }
    // Start on the first method this service offers.
    setMethod(methods[0].type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleApply = () => {
    // Every row this trip added, not just the first one. Three buttons
    // picked together on the service screen share one location and one note
    // (Alteration.jsx stamps each row it adds with the same `added_batch`).
    const batch = sessionStorage.getItem("addedBatch");
    const chosenPiece = (() => {
      try {
        return JSON.parse(sessionStorage.getItem("selectedItems"));
      } catch {
        return null;
      }
    })();
    const addedThisTrip = (service) =>
      (batch && service?.added_batch === batch) ||
      (service?.name === sessionStorage.getItem("option") &&
        chosenPiece?.id == service?.item_details?.id);
    const updatedServices = cartItems[0]?.services?.map((service) => {
      if (addedThisTrip(service)) {
        return {
          ...service,
          // No measurement: the fit notes go in item_notes on the next
          // screen, so the admin shows just the method here.
          alterationDetails: { type: method },
        };
      }
      return service;
    });
    if (updatedServices && cartItems[0]?.id) {
      updateCartItem(cartItems[0].id, { services: updatedServices });
    }
    navigate("/know-more", {
      state: {
        selectedOption: location.state?.selectedOption,
        selectedTask: location.state?.selectedTask,
        data: location.state?.data,
        fitMethod: method,
      },
    });
  };

  const summary = (
    <>
      <div className="show-box-list">
        <p className="show-box-para">Your items</p>
        <h4 className="show-box-answer">
          {location?.state?.data?.selectedItem
            ? location?.state?.data?.selectedItem?.name
            : sessionStorage.getItem("selectedItem")}
        </h4>
      </div>

      <div className="show-box-list">
        <p className="show-box-para">Services</p>
        <h4 className="show-box-answer">
          {location?.state?.data?.addNewService
            ? location?.state?.data?.selectedItem?.pricing_plan
            : sessionStorage.getItem("SelectedPlan")}
        </h4>
      </div>

      <div className="show-box-last-list">
        <p className="show-box-para">Task Detail</p>
        <h4 className="show-box-answer">{sessionStorage.getItem("task")}</h4>
        <div className="service-job-detail-box">
          <p className="show-box-para">{sessionStorage.getItem("option")}</p>
          {selectedOptionPrice != "" && (
            <div className="text-box">
              <h4 className="show-box-answer">${selectedOptionPrice}</h4>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <section className="items-section same-bg-section relative">
      <div className="container padding-x-all">
        <div className="items-box sec-com-padding flex-box justify-between align-center">
          <CartSummary
            className="mobile-none"
            isOpen={!isOpen}
            onToggle={toggleBox}
          >
            {summary}
          </CartSummary>

          <CartSummary
            className="desktop-none"
            isOpen={!isOpen1}
            onToggle={toggleBox}
          >
            {summary}
          </CartSummary>

          <div className="item-select-box">
            <h2>Fit details</h2>
            <p className="para">
              How was the fit captured? Notes for the fixer go on the next
              screen.
            </p>

            <div className="perfect-fit-box flex-box justify-between">
              {availableMethods.length > 0 && (
                <div className="tabs-box">
                  {availableMethods.map((m) => (
                    <button
                      key={m.type}
                      type="button"
                      onClick={() => setMethod(m.type)}
                      className={method === m.type ? "item-selected" : ""}
                    >
                      <img src={ICONS[m.type]} alt="" />
                      <h6>{m.label}</h6>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              className="section-main-btn"
              type="button"
              onClick={handleApply}
            >
              Next
              <span>
                <img src={btnArrow} alt="" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PerfectFit;
